import dateHandler from '@/utils/dateHandler';
import { useI18n } from 'vue-i18n';

export function filterMissionsByYearAndWeekDays(missions, week) {
  let weekDates = dateHandler.splitStringInDates(week);
  let startDate = dateHandler.transformHumanDateToUnix(weekDates[0]);
  let endDate = dateHandler.transformHumanDateToUnix(weekDates[1]) + 86399; // end of the day;
  return Object.fromEntries(
    Object.entries(missions)
      .map(([key, value]) => [
        key,
        { id: key, ...value }
      ])
      .filter(([key, value]) => value.date_start >= startDate && value.date_start <= endDate)
  );
}

const ALLOWED_CRA_STATUSES = [null, 'request', 'approved'];

export function filterCrasByYearAndWeekDays(cras, week) {
  if (!cras || !week) return {};
  let weekDates = dateHandler.splitStringInDates(week);
  let startDate = dateHandler.transformHumanDateToUnix(weekDates[0]);
  let endDate = dateHandler.transformHumanDateToUnix(weekDates[1]) + 86399; // end of the day
  return Object.fromEntries(
    Object.entries(cras)
      .map(([key, value]) => [key, { ...value, _storeKey: key, id: value.id || key, date_start: parseInt(value.date_start), date_end: parseInt(value.date_end) }])
      // Include events that overlap with the week range (not just those starting in it)
      .filter(([key, value]) => ALLOWED_CRA_STATUSES.includes(value.status) && value.date_end >= startDate && value.date_start <= endDate)
  );
}

export function groupMissionsByWeekDays(missions, filter, planningCras = []) {
  const weekDates = dateHandler.splitStringInDates(filter.selectedWeek.value);
  const startWeekDay = weekDates[0];
  const endWeekDay = weekDates[1];
  const weekDays = dateHandler.getLocaleWeekDays(startWeekDay, endWeekDay);
  const sortedMissions = missions.sort((a, b) => a.date_start - b.date_start);
  let result = {};
  Object.keys(weekDays).forEach((weekDayDate) => {
    const dayItems = [];
    Object.values(sortedMissions).forEach((mission) => {
      let missionDate = dateHandler.convertUnixTimestampToDate(mission.date_start);
      if (missionDate === weekDayDate) {
        dayItems.push({ ...mission, _type: 'mission' });
      }
    });
    if (planningCras && planningCras.length) {
      const dayStartUnix = dateHandler.transformHumanDateToUnix(weekDayDate);
      const dayEndUnix = dayStartUnix + 86399;
      planningCras.forEach((cra) => {
        const craStart = parseInt(cra.date_start);
        const craEnd = parseInt(cra.date_end);
        // Check if this CRA event overlaps with the current day
        if (craStart <= dayEndUnix && craEnd >= dayStartUnix) {
          // Compute multi-day metadata
          const startDay = dateHandler.convertUnixTimestampToDate(craStart);
          const endDay = dateHandler.convertUnixTimestampToDate(craEnd);
          const isMultiDay = startDay !== endDay;
          let dayIndex = 0;
          let totalDays = 1;
          if (isMultiDay) {
            const startMoment = window.$moment(startDay, 'YYYY-MM-DD');
            const endMoment = window.$moment(endDay, 'YYYY-MM-DD');
            const currentMoment = window.$moment(weekDayDate, 'YYYY-MM-DD');
            totalDays = endMoment.diff(startMoment, 'days') + 1;
            dayIndex = currentMoment.diff(startMoment, 'days');
          }
          dayItems.push({
            ...cra,
            date_start: craStart,
            date_end: craEnd,
            _type: 'cra',
            _isMultiDay: isMultiDay,
            _dayIndex: dayIndex,
            _totalDays: totalDays,
          });
        }
      });
    }
    dayItems.sort((a, b) => a.date_start - b.date_start);
    result[weekDays[weekDayDate]] = dayItems;
  });
  return result;
}

export function extractMissionsIdByPosId(missions, posId) {
  const matchingKeys = [];
  for (const key in missions) {
    if (missions.hasOwnProperty(key) && missions[key]?.pos?.id === posId) {
      matchingKeys.push(parseInt(key));
    }
  }
  return matchingKeys;
}

// assuming there's only one configuration per pos id
export function extractConfigurationIdByPosId(missions, posId) {
  let configurationId = null;
  for (const key in missions) {
    if (missions.hasOwnProperty(key) && missions[key]?.pos?.id.toString() === posId.toString()) {
      configurationId = missions[key]['configuration']['id'];
    }
  }
  return configurationId;
}

export function extractMissionsPlanningsByMission(missionsPlannings, missionIds) {
  const matchingKeys = [];
  for (const key in missionsPlannings) {
    if (missionsPlannings.hasOwnProperty(key) && missionsPlannings[key] && missionIds.includes(missionsPlannings[key]?.mission?.id)) {
      matchingKeys.push({id:key, ...missionsPlannings[key]});
    }
  }
  return Object.values(matchingKeys);
}

export function copyMissionsKeysInsideOfTheObject(missions)
{
  const resultObject = [];
  for (const key in missions) {
    resultObject.push({ id: Number(key), ...missions[key] });
  }
  return resultObject;
}

export function filterMissionsByConfigurationId(missions, configurationId) {
  return Object.keys(missions).filter((missionId) => {
    const mission = missions[missionId];
    return mission.configuration && mission.configuration.id == configurationId;
  }).reduce((result, missionId) => {
    result[missionId] = missions[missionId];
    return result;
  }, {});
}

export function posItemTitle(pos, mission, tr) {
  return `${pos?.name} - ${dateHandler.convertUnixTimestampToHumanDate(mission?.last_reading_date || 0) || tr('global.text.none.m')}`;
}

export function groupedPointsOfSaleList(pointsOfSale, plannedPosIds, missions, configurationId, t) {
  const plannedVisits = Object.fromEntries(
    Object.entries(pointsOfSale).filter(([key, val]) => plannedPosIds.includes(key.toString()))
  );
  const notPlannedVisits = Object.fromEntries(
    Object.entries(pointsOfSale).filter(([key, val]) => !plannedPosIds.includes(key.toString()))
  );

  let items= [];
  if (Object.values(plannedVisits).length) {
    items.push({ props: { header: t('appointment.new.modal.pdv-group.planned') }});
    items.push(...Object.values(plannedVisits).filter((pos) => {
    return Object.values(missions).some(m => m?.pos?.id?.toString() === pos?.id?.toString() &&
      m?.configuration?.id.toString() === configurationId.toString())
    }).map((pos) => {
      const posMission = Object.values(missions).find(m => m?.pos?.id?.toString() === pos?.id?.toString());
      if (posMission) {
        return { title: posItemTitle(pos, posMission, t), value: posMission.id, tagPos: pos.tag_pos || []};
      }
    }))
  }

  if (Object.values(notPlannedVisits).length) {
    items.push({ props: { header: t('appointment.new.modal.pdv-group.not-planned') }});
    items.push(...Object.values(notPlannedVisits).filter((pos) => {
      return Object.values(missions).some(m => m?.pos?.id?.toString() === pos?.id?.toString() &&
        m?.configuration?.id.toString() === configurationId.toString())
    }).map((pos) => {
      const posMission = Object.values(missions).find(m => m?.pos?.id?.toString() === pos?.id?.toString());
      if (posMission) {
        return { title: posItemTitle(pos, posMission, t), value: posMission.id.toString(), tagPos: pos.tag_pos || []};
      }
    }))
  }
  return items;
}

export function findMissionIdByPos(missions, targetPosId) {
  for (const key in missions) {
    if (missions[key]?.pos && missions[key]?.pos?.id?.toString() === targetPosId?.toString()) {
      return key;
    }
  }
  return null;
}

export function getFuturePlannedMissionsPosIdsFromMissionPlannings(missionsPlannings, missions) {
  const missionsIdsHavingAMissionPlanning = Object.values(missionsPlannings)
    .filter(missionPlanning => parseInt(missionPlanning.date_start || 0) > window.$moment().unix())
    .map(missionPlanning => missionPlanning.mission.id.toString());
  return Object.entries(missions)
    ?.filter(([missionId, mission]) => missionsIdsHavingAMissionPlanning.includes(missionId.toString()))
    ?.map(([missionId, mission]) => mission.pos.id?.toString()) || [];
}